import React from "react";
import type { SendableSubmission } from "@/types/fitconnect";
import { LoadingCard } from "@/components/LoadingCard.tsx";

interface MessageFormProps {
  onSendMessage: (message: SendableSubmission) => void;
  isLoading: boolean;
}

const MessageForm = ({ onSendMessage, isLoading }: MessageFormProps) => {
  const [content, setContent] = React.useState(
    "{\n" +
      '  "antragsteller": {\n' +
      '    "nachname": "Musterfrau",\n' +
      '    "vorname": "Erika",\n' +
      '    "geburtsdatum": "1990-05-12",\n' +
      '    "adresse": {\n' +
      '      "strasseHausnummer": "Musterstrasse 42",\n' +
      '      "plz": "12345",\n' +
      '      "ort": "Musterhausen"\n' +
      "    }\n" +
      "  },\n" +
      '  "antrag": "Test"\n' +
      "}",
  );
  const [subject, setSubject] = React.useState("ba2a65ed-0678-4e56-8d8b-8e96ecf5fa54");
  const [serviceIdentifier, setServiceIdentifier] = React.useState(
    "urn:de:fim:leika:leistung:99400048079000",
  );
  const [attachments, setAttachments] = React.useState<File[]>([]);
  const [isXml, setIsXml] = React.useState(false);
  const [schemaUri, setSchemaUri] = React.useState(
    "https://schema.fitko.de/demo/muster-fachdaten.schema.json",
  );
  const [enableReply, setEnableReply] = React.useState(false);
  const [replyProcessStandard, setReplyProcessStandard] = React.useState(
    "https://schema.fitko.de/demo/muster-prozess-standard.xml",
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    onSendMessage({
      subject,
      content,
      serviceIdentifier,
      attachments,
      isXml,
      schemaUri,
      enableReply,
      replyProcessStandard,
    });
  };

  return (
    <div className="relative">
      {isLoading && <LoadingCard msg="Versende..." />}

      <form onSubmit={handleSubmit}>
        <section className="card shadow-sm bg-white">
          <div className="card-body">
            <fieldset className="fieldset">
              <legend className="fieldset-legend">Empfänger</legend>
              <input
                disabled={isLoading}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                className="input w-full"
                placeholder="Enter destination UUID"
              />
            </fieldset>

            <fieldset className="fieldset">
              <legend className="fieldset-legend">
                Verwaltungsleistung (LeiKa)
              </legend>
              <input
                disabled={isLoading}
                value={serviceIdentifier}
                onChange={(e) => setServiceIdentifier(e.target.value)}
                required
                className="input w-full"
                placeholder="urn:de:fim:leika:leistung:99400048079000"
              />
            </fieldset>

            <fieldset className="fieldset">
              <legend className="fieldset-legend">
                Fachdaten-Schema (URN)
              </legend>
              <input
                disabled={isLoading}
                value={schemaUri}
                onChange={(e) => setSchemaUri(e.target.value)}
                required
                className="input w-full"
                placeholder="URN der Leistung"
              />
            </fieldset>

            <fieldset className="fieldset">
              <legend className="fieldset-legend">Datenformat</legend>

              <div className="flex gap-5">
                <label className="cursor-pointer">
                  <input
                    type="radio"
                    className="radio"
                    checked={!isXml}
                    onChange={() => setIsXml(false)}
                  />
                  <span className="ml-2">JSON</span>
                </label>
                <label className="cursor-pointer">
                  <input
                    type="radio"
                    className="radio"
                    checked={isXml}
                    onChange={() => setIsXml(true)}
                  />
                  <span className="ml-2">XML</span>
                </label>
              </div>
            </fieldset>

            <fieldset className="fieldset">
              <legend className="fieldset-legend">
                Fachdaten ({isXml ? "XML" : "JSON"})
              </legend>
              <textarea
                disabled={isLoading}
                rows={10}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                required
                className="textarea w-full"
                placeholder={`Geben Sie die Fachden im ${isXml ? "XML" : "JSON"} Format ein`}
              />
            </fieldset>

            <fieldset className="fieldset">
              <legend className="fieldset-legend">Anhänge</legend>
              <input
                type="file"
                className="file-input file-input-ghost"
                multiple
                onChange={(e) =>
                  setAttachments(
                    e.target.files ? Array.from(e.target.files) : [],
                  )
                }
                disabled={isLoading}
              />
              <label className="label">
                Sie können mehrere Dateien anhängen
              </label>
            </fieldset>

            <fieldset className="fieldSet">
              <legend className="fieldset-legend">
                Mit Rückkanal "FIT-Connect"
              </legend>

              <div className="flex gap-5">
                <label className="cursor-pointer">
                  <input
                    type="radio"
                    className="radio"
                    checked={enableReply}
                    onChange={() => setEnableReply(true)}
                  />
                  <span className="ml-2">Ja</span>
                </label>
                <label className="cursor-pointer">
                  <input
                    type="radio"
                    className="radio"
                    checked={!enableReply}
                    onChange={() => setEnableReply(false)}
                  />
                  <span className="ml-2">Nein</span>
                </label>
              </div>
            </fieldset>

            {enableReply && (
              <fieldset className="fieldset">
                <legend className="fieldset-legend">
                  Prozess-Standard (URN)
                </legend>
                <input
                  disabled={isLoading}
                  value={replyProcessStandard}
                  onChange={(e) => setReplyProcessStandard(e.target.value)}
                  required={enableReply}
                  className="input w-full"
                  placeholder="URN eines formalen Beschreibung des Austauschprozesses"
                />
              </fieldset>
            )}
          </div>
        </section>

        <button
          type="submit"
          disabled={isLoading}
          className="btn btn-primary w-full mt-5"
        >
          Nachricht versenden
        </button>
      </form>
    </div>
  );
};

export default MessageForm;
